import type { ApiResponse } from '@/libs/interfaces'

const rawApiBaseUrl: unknown = import.meta.env.VITE_API_BASE_URL
const API_BASE_URL =
  typeof rawApiBaseUrl === 'string' && rawApiBaseUrl.length > 0 ? rawApiBaseUrl : '/api'

interface RequestConfig extends Omit<RequestInit, 'body'> {
  params?: Record<string, string>
  body?: unknown
  signal?: AbortSignal
}

class ApiError<T = unknown> extends Error {
  status: number
  data?: T

  constructor(message: string, status: number, data?: T) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

const getAuthToken = () => localStorage.getItem('authToken')
const getRefreshToken = () => localStorage.getItem('refreshToken')

export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem('authToken', token)
  } else {
    localStorage.removeItem('authToken')
  }
}

export const setRefreshToken = (token: string | null) => {
  if (token) localStorage.setItem('refreshToken', token)
  else localStorage.removeItem('refreshToken')
}

const buildUrl = (endpoint: string, params?: Record<string, string>) => {
  const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin)
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      url.searchParams.append(k, v)
    })
  }
  return url.toString()
}

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  const isJson = response.headers.get('content-type')?.includes('application/json')
  const data: unknown = isJson ? await response.json() : await response.text()
  const dataObj = data as Record<string, unknown>

  if (!response.ok) {
    throw new ApiError(
      (typeof dataObj?.message === 'string' ? dataObj.message : null) ||
        `HTTP error ${response.status}`,
      response.status,
      data,
    )
  }

  return {
    data: data as T,
    status: response.status,
    message: typeof dataObj?.message === 'string' ? dataObj.message : undefined,
  }
}

// Queue to hold pending requests while a token refresh is in progress
let isRefreshing = false
let refreshQueue: Array<(token: string | null) => void> = []

const processRefreshQueue = (token: string | null) => {
  refreshQueue.forEach((resolve) => resolve(token))
  refreshQueue = []
}

const doTokenRefresh = async (): Promise<string | null> => {
  const currentRefreshToken = getRefreshToken()
  if (!currentRefreshToken) return null

  const res = await fetch(`${API_BASE_URL}/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: currentRefreshToken }),
  })

  const data = (await res.json()) as Record<string, unknown>

  if (!res.ok) throw new Error('Refresh failed')

  const newToken = typeof data.accessToken === 'string' ? data.accessToken : ''
  setAuthToken(newToken)

  if (typeof data.refreshToken === 'string') {
    setRefreshToken(data.refreshToken)
  }

  return newToken
}

const refreshAccessToken = async (): Promise<string | null> => {
  if (isRefreshing) {
    // Queue this request until the in-flight refresh resolves
    return new Promise<string | null>((resolve) => {
      refreshQueue.push(resolve)
    })
  }

  isRefreshing = true

  try {
    const newToken = await doTokenRefresh()
    if (!newToken) {
      processRefreshQueue(null)
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = '/login'
      return null
    }
    processRefreshQueue(newToken)
    return newToken
  } catch {
    processRefreshQueue(null)
    localStorage.clear()
    sessionStorage.clear()
    window.location.href = '/login'
    return null
  } finally {
    isRefreshing = false
  }
}

const createRequest = async <T>(
  endpoint: string,
  config: RequestConfig = {},
  retry = true,
): Promise<ApiResponse<T>> => {
  const { params, body, signal, ...rest } = config

  const url = buildUrl(endpoint, params)
  const token = getAuthToken()

  const isFormData = body instanceof FormData

  const headers: HeadersInit = {
    ...rest.headers,
    ...(body !== undefined && !isFormData && { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
  }

  const response = await fetch(url, {
    ...rest,
    headers,
    signal,
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
  })

  if ((response.status === 401 || response.status === 403) && retry) {
    const newToken = await refreshAccessToken()

    if (newToken) {
      try {
        return await createRequest(endpoint, config, false)
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          localStorage.clear()
          sessionStorage.clear()
          window.location.href = '/login'
        }
        throw error
      }
    }
  }

  return handleResponse<T>(response)
}

export const api = {
  get: <T>(endpoint: string, params?: Record<string, string>, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'GET', params, signal }),

  post: <T>(endpoint: string, data?: unknown, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'POST', body: data, signal }),

  put: <T>(endpoint: string, data?: unknown, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'PUT', body: data, signal }),

  patch: <T>(endpoint: string, data?: unknown, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'PATCH', body: data, signal }),

  delete: <T>(endpoint: string, signal?: AbortSignal) =>
    createRequest<T>(endpoint, { method: 'DELETE', signal }),
}

export { ApiError }
